import type { ChatMessage } from './frames';
import { parseSystemChatMeta } from './frames';

/**
 * Chat history paging over REST: GET /api/v1/rooms/{room}/messages with the join JWT as a
 * bearer token (docs/contract.md). `before` selects the page strictly older than that message
 * id; the response is ascending by id, hasMore announces that still older messages exist.
 */
export interface HistoryPage {
  readonly messages: readonly ChatMessage[];
  readonly hasMore: boolean;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function parseMessage(value: unknown): ChatMessage | null {
  if (!isRecord(value)) {
    return null;
  }
  const id = value.id;
  const body = value.body;
  const ts = value.ts;
  if (
    typeof id !== 'number' ||
    !Number.isInteger(id) ||
    typeof body !== 'string' ||
    typeof ts !== 'string'
  ) {
    return null;
  }
  return {
    id,
    identity: typeof value.identity === 'string' ? value.identity : '',
    displayName: typeof value.displayName === 'string' ? value.displayName : '',
    body,
    ts,
    system: value.system === true,
    ...parseSystemChatMeta(value),
  };
}

/** Narrows an unknown response body to a HistoryPage; throws a TypeError on a shape violation. */
export function parseHistoryResponse(body: unknown): HistoryPage {
  if (!isRecord(body)) {
    throw new TypeError('history response must be a JSON object');
  }
  if (!Array.isArray(body.messages) || typeof body.hasMore !== 'boolean') {
    throw new TypeError('history response must carry "messages" and "hasMore"');
  }
  const messages: ChatMessage[] = [];
  for (const entry of body.messages) {
    const message = parseMessage(entry);
    if (message === null) {
      throw new TypeError('history response message violates the chat row shape');
    }
    messages.push(message);
  }
  return { messages, hasMore: body.hasMore };
}

export class HistoryApiError extends Error {
  constructor(readonly status: number) {
    super(`chat history request failed with HTTP ${status}`);
    this.name = 'HistoryApiError';
  }
}

/** Page size of the history REST endpoint (contract default, server caps and defaults at 50). */
export const CHAT_HISTORY_PAGE_SIZE = 50;

/**
 * Fetches one history page. `beforeId: null` asks for the newest window (the same one the WS
 * auth handshake replays) and is only used as a fallback; the chat panel pages with the oldest
 * loaded id.
 */
export async function fetchChatHistory(
  room: string,
  token: string,
  beforeId: number | null,
): Promise<HistoryPage> {
  const params = new URLSearchParams({ limit: String(CHAT_HISTORY_PAGE_SIZE) });
  if (beforeId !== null) {
    params.set('before', String(beforeId));
  }
  const response = await fetch(
    `/api/v1/rooms/${encodeURIComponent(room)}/messages?${params.toString()}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!response.ok) {
    throw new HistoryApiError(response.status);
  }
  const body: unknown = await response.json();
  return parseHistoryResponse(body);
}
